'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  LayoutDashboard, Calendar, Clock, Heart, Bell, MessageCircle,
  User, Settings, LogOut, Menu, X, ChevronRight
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/dashboard' },
  { icon: Calendar, label: 'Upcoming Rituals', href: '/dashboard/upcoming' },
  { icon: Clock, label: 'Past Rituals', href: '/dashboard/past' },
  { icon: Heart, label: 'Favourite Priests', href: '/dashboard/favourites' },
  { icon: Bell, label: 'Notifications', href: '/dashboard/notifications', badge: 3 },
  { icon: MessageCircle, label: 'Messages', href: '/dashboard/messages', badge: 1 },
  { icon: User, label: 'Profile', href: '/dashboard/profile' },
  { icon: Settings, label: 'Settings', href: '/dashboard/settings' },
];

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Devotee';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/login');
    } catch {}
  };

  const currentPage = navItems.find(item => item.href === pathname)?.label || 'Dashboard';

  return (
    <div className="min-h-screen bg-ivory flex">
      {/* Sidebar Overlay (mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-gold/20 z-50 flex flex-col transition-transform duration-300 shadow-xl
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static md:flex md:shadow-none`}
        role="navigation"
        aria-label="Dashboard navigation"
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gold/15">
          <Link href="/home-screen" className="flex items-center gap-2.5 focus-ring rounded-lg" aria-label="Yagnick home">
            <div className="w-8 h-8 rounded-full saffron-gradient flex items-center justify-center shadow-sm" aria-hidden="true">
              <span className="text-white text-base">🪔</span>
            </div>
            <span className="font-display text-lg font-bold text-maroon">Yagnick</span>
          </Link>
          <button
            className="md:hidden text-maroon/50 hover:text-maroon transition-colors focus-ring rounded p-1"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X size={18} />
          </button>
        </div>

        {/* User Info */}
        <div className="px-5 py-4 border-b border-gold/15">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full saffron-gradient flex items-center justify-center shadow-sm flex-shrink-0" aria-hidden="true">
              <span className="text-white text-sm font-bold">{initials}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-maroon truncate">{displayName}</p>
              <p className="text-xs text-maroon/50 truncate">{user?.email || ''}</p>
            </div>
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" aria-label="Dashboard sections">
          {navItems.map((item) => {
            const isActive = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setSidebarOpen(false)}
                aria-current={isActive ? 'page' : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group focus-ring
                  ${isActive
                    ? 'bg-saffron/10 text-saffron border border-saffron/20' :'text-maroon/60 hover:text-maroon hover:bg-cream border border-transparent'
                  }`}
              >
                <item.icon size={17} strokeWidth={isActive ? 2.2 : 1.8} aria-hidden="true" />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="w-5 h-5 rounded-full bg-saffron text-white text-[10px] font-bold flex items-center justify-center" aria-label={`${item.badge} unread`}>
                    {item.badge}
                  </span>
                )}
                {isActive && <ChevronRight size={14} className="text-saffron/60" aria-hidden="true" />}
              </Link>
            );
          })}
        </nav>

        {/* Sign Out */}
        <div className="px-3 py-4 border-t border-gold/15">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-maroon/50 hover:text-red-600 hover:bg-red-50 transition-all duration-150 focus-ring"
            aria-label="Sign out of your account"
          >
            <LogOut size={17} strokeWidth={1.8} aria-hidden="true" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-gold/15 px-4 md:px-6 py-3.5 flex items-center gap-4">
          <button
            className="md:hidden text-maroon/60 hover:text-maroon transition-colors focus-ring rounded p-1"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={sidebarOpen}
          >
            <Menu size={22} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-maroon truncate">{currentPage}</h1>
          </div>
          <Link
            href="/booking-flow"
            className="hidden sm:flex items-center gap-2 bg-saffron hover:bg-saffron/90 text-white text-sm font-semibold px-4 py-2 rounded-full btn-glow transition-all focus-ring"
            aria-label="Book a new ritual"
          >
            <Calendar size={15} aria-hidden="true" />
            Book a Ritual
          </Link>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto" id="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
